import React, {useEffect} from "react";
import '../../styles/common/button.scss';
import SpinnerCircle from "./SpinnerCircle";
import PianoKey, {Key} from "./PianoKey";
import {midiUserAction} from "../../services/midi/midi-user-action";
import {useAppSelector} from "../../redux/hooks";
import {settingsStore} from "../../redux/settings/settingsStore";

type ButtonType = "primary" | "secondary";

export interface IButtonProps {
  additionalClassName?: string;
  ariaLabel?: string;
  buttonType? : ButtonType;
  children?: React.ReactNode
  disabled?: boolean;
  icon?: React.ReactElement;
  isLoading?: boolean;
  minWidth?: number;
  navigationNote?: Key;
  onClick: () => void;
  text: string | React.ReactNode;
}

export default function Button(props: IButtonProps) {
  // Often times on larger devices we want to put a bunch of whitespace around the text of the button. It will look good
  // on a larger device but on smaller devices it will sometimes cause the button to be too wide to fit on the screen.
  // So we need to make sure that the button fits on the screen on smaller devices.
  let adjustedMinWidth = props.minWidth;
  if (props.minWidth) {
    const paddingAllowance = 20;
    const viewPortWidth = window.innerWidth;
    const maxAllowedWidth = viewPortWidth - paddingAllowance;
    adjustedMinWidth = Math.min(props.minWidth, maxAllowedWidth);
  }

  const { navigationNote, onClick } = props;
  const isMidiKeyboardConnected = useAppSelector(settingsStore.getIsMidiDeviceConnected);

  useEffect(() => {
    if (navigationNote) {
      return midiUserAction.subscribeToNextMidiNotePlayed(navigationNote, onClick);
    }
  }, [navigationNote, onClick]);

  const newProps = {...props, minWidth: adjustedMinWidth};
  return navigationNote && isMidiKeyboardConnected
    ? getButtonWithPianoSupport(newProps, navigationNote)
    : getStandardButton(newProps);
}

function getButtonWithPianoSupport(props: IButtonProps, navigationNote: Key) {
  const newChildren = <div className="inner">
    <div className="innerContainer">
      {props.icon ? props.icon : null}
      <div className="text">{props.text}</div>
      <PianoKey pianoKey={navigationNote}/>
    </div>
    {
      props.children
        ? <div>{props.children}</div>
        : null
    }
  </div>

  props.additionalClassName += " navigation";

  return nativeButton(props, newChildren);
}

function getStandardButton(props: IButtonProps) {
  const newChildren = <>
    {props.icon ? props.icon : null}
    {props.text}
    {props.children}
  </>

  return nativeButton(props, newChildren)
}

function nativeButton({ ariaLabel, onClick, disabled, minWidth, buttonType="primary", additionalClassName = '', isLoading}: IButtonProps, children: React.ReactNode){
  const disabledClassName = disabled ? "disabled" : "";
  const accessibleLabel = ariaLabel ? ariaLabel : "";
  const primarySecondaryClassName = buttonType === "primary" ? "primary" : "secondary";
  const className = `button ${primarySecondaryClassName} ${additionalClassName} ${disabledClassName}`;

  if (isLoading) {
    children = <SpinnerCircle/>;
  }

  return <button
    style={minWidth ? {minWidth: minWidth} : {}}
    type="submit"
    className={className}
    aria-label={accessibleLabel}
    onClick={() => {
      if (!disabled) {
        onClick();
      }
    }}>
    {children}
  </button>;
}
